import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr(t("reset.errMinLen"));
      return;
    }
    if (password !== confirm) {
      setErr(t("reset.errMismatch"));
      return;
    }
    if (!token) {
      setErr(t("reset.errNoToken"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data?.token) localStorage.setItem("progres_token", data.token);
      await refreshMe();
      setDone(true);
      toast.success(t("reset.successToast"));
      setTimeout(() => nav("/", { replace: true }), 1500);
    } catch (ex) {
      setErr(ex.response?.data?.detail || t("reset.errFallback"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md animate-fade-in">
        <div className="mb-10 text-center">
          <div className="font-display text-5xl leading-none text-stone-900">progres.</div>
        </div>
        <div className="card-soft p-8" data-testid="reset-card">
          {done ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-600 mx-auto flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
              <h1 className="font-display text-2xl text-stone-900 mb-2">{t("reset.doneTitle")}</h1>
              <p className="text-sm text-stone-500">{t("reset.doneBody")}</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl text-stone-900 mb-2 leading-tight">
                {t("reset.title")}
              </h1>
              <p className="text-sm text-stone-500 mb-8">{t("reset.minChars")}</p>
              <form onSubmit={onSubmit} className="space-y-4">
                <input
                  data-testid="reset-password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.newPassword")}
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none"
                />
                <input
                  data-testid="reset-confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={t("auth.passwordConfirm")}
                  className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none"
                />
                {err && (
                  <div data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={busy}
                  className="btn-pill w-full disabled:opacity-50"
                >
                  {busy ? t("reset.busy") : t("reset.setBtn")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
              <div className="mt-6 text-center text-sm text-stone-500">
                <Link to="/login" className="text-stone-900 font-semibold hover:underline underline-offset-4">
                  {t("auth.backToLogin")}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
